<template>
  <div :class="viewMode === 'list' ? 'bg-white dark:bg-gray-900 shadow overflow-hidden rounded-lg sm:rounded-md' : ''">
    <!-- Empty State -->
    <EmptyState
      v-if="orders.length === 0"
      icon="document"
      :title="$t('app.views.orders.no_orders')"
      :description="$t('app.views.orders.no_orders_description')"
      variant="minimal"
      size="md"
    >
      <template #actions>
        <button 
          type="button" 
          class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" 
          @click="$emit('new-order')"
        >
          <PlusIcon class="-ml-1 mr-2 h-5 w-5" />
          {{ $t('app.views.orders.new_order') }}
        </button>
      </template>
    </EmptyState>
    
    <!-- Orders List -->
    <ul
      v-if="viewMode === 'list'"
      role="list"
      class="divide-y divide-gray-200 dark:divide-gray-700"
    >
      <OrderListItem
        v-for="order in orders"
        :key="order.id"
        :order="order"
        @view="$emit('view', $event)"
        @edit="$emit('edit', $event)"
        @complete="$emit('complete', $event)"
        @cancel="$emit('cancel', $event)"
      />
    </ul>

    <!-- Orders Grid (Card View - Desktop only) -->
    <div
      v-else-if="viewMode === 'card'"
      class="sm:columns-1 md:columns-2 lg:columns-3 xl:columns-4 column-gap-2 sm:column-gap-3"
    >
      <OrderCard
        v-for="order in orders"
        :key="order.id"
        :order="order"
        class="break-inside-avoid mb-3 sm:mb-4"
        @view="$emit('view', $event)"
        @edit="$emit('edit', $event)"
        @complete="$emit('complete', $event)"
        @cancel="$emit('cancel', $event)"
      />
    </div>

    <!-- Mobile fallback: show list view on mobile when in card mode -->
    <ul
      v-else-if="viewMode === 'card'"
      class="sm:hidden divide-y divide-gray-200 dark:divide-gray-700"
    >
      <OrderListItem
        v-for="order in orders"
        :key="order.id"
        :order="order"
        @view="$emit('view', $event)"
        @edit="$emit('edit', $event)"
        @complete="$emit('complete', $event)"
        @cancel="$emit('cancel', $event)"
      />
    </ul>
  </div>
</template>

<script setup lang="ts">
import { PlusIcon } from '@heroicons/vue/24/outline';
import EmptyState from '@/components/base/EmptyState.vue';
import OrderListItem from './OrderListItem.vue';
import OrderCard from './OrderCard.vue';
import type { OrderWithLocalFields } from '@/utils/orderHelpers';

defineProps<{
  orders: OrderWithLocalFields[];
  viewMode: 'list' | 'card';
}>();

defineEmits<{
  'new-order': [];
  view: [order: OrderWithLocalFields];
  edit: [order: OrderWithLocalFields];
  complete: [orderId: number];
  cancel: [orderId: number];
}>();
</script>
